const normalize = (value) => String(value || '').trim()

const normalizeForComparison = (value) => normalize(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

export const AVAILABILITY_DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
]

export const AVAILABILITY_PERIODS = ['Morning', 'Afternoon', 'Evening']

export const getSelectedAvailabilityDays = (availability = {}) => (
    AVAILABILITY_DAYS.filter((day) =>
        AVAILABILITY_PERIODS.some((period) => availability?.[day]?.[period] === true)
    )
)

export const formatAvailabilityForCopy = (availability = {}) => (
    getSelectedAvailabilityDays(availability)
        .map((day) => {
            const selectedPeriods = AVAILABILITY_PERIODS.filter(
                (period) => availability?.[day]?.[period] === true
            )
            return `${day}: ${selectedPeriods.join(', ')}`
        })
        .join('\n')
)

export const formatFamilyAddress = (address) => {
    if (typeof address === 'string') return normalize(address)
    if (!address || typeof address !== 'object') return ''

    const lineOne = [address.addressLine1, address.addressLine2]
        .map(normalize)
        .filter(Boolean)
        .join(' ')
    const cityState = [address.city, address.state]
        .map(normalize)
        .filter(Boolean)
        .join(', ')
    const locality = [cityState, normalize(address.zipcode)]
        .filter(Boolean)
        .join(' ')
    return [lineOne, locality].filter(Boolean).join(', ')
}

export const formatParentName = (familyInfo = {}) => {
    const parentName = normalize(familyInfo.primaryParentName)
    const familyName = normalize(familyInfo.familyName)
    if (!parentName) return familyName
    if (!familyName) return parentName

    const normalizedParent = normalizeForComparison(parentName)
    const normalizedFamily = normalizeForComparison(familyName)
    return normalizedParent.includes(normalizedFamily)
        ? parentName
        : `${parentName} ${familyName}`
}

export const buildFamilyDetailRows = ({ familyInfo = {}, modalInfo = {} } = {}) => {
    const parentName = formatParentName(familyInfo)
    const message = normalize(modalInfo.todoDescription)
        || normalize(familyInfo.initialAvailabilityNotes)

    return [
        {
            id: 'parent',
            label: 'Parent name',
            value: parentName,
            displayValue: parentName ? `${parentName} (Parent)` : '',
        },
        {
            id: 'email',
            label: 'Email',
            value: normalize(familyInfo.primaryEmail),
        },
        {
            id: 'phone',
            label: 'Phone number',
            value: normalize(familyInfo.primaryCellphone),
        },
        {
            id: 'address',
            label: 'Address',
            value: formatFamilyAddress(familyInfo.address),
        },
        {
            id: 'message',
            label: 'Message',
            value: message,
        },
    ].filter((row) => row.value)
}

const teacherProfileBaseBySchool = {
    mma: 'https://www.mountainsidemusicacademy.com/music-teachers',
    lsma: 'https://www.lakesidemusicacademy.com/instructors',
    tma: 'https://www.treesidemusicacademy.com/instructors',
    mor: 'https://www.morningsidemusicacademy.com/instructors',
    psma: 'https://www.parksidemusicacademy.com/instructors',
    tem: 'https://www.atharomusic.com/tempe-music-academy/instructors',
    den: 'https://www.atharomusic.com/denton-music-academy/instructors',
    tul: 'https://www.atharomusic.com/tulsa-music-academy/instructors',
}

export const getTeacherProfileUrl = (teacher = {}) => {
    const teacherName = normalize(teacher.teacherName || teacher.Name)
    const school = normalize(teacher.school || teacher.School).toLowerCase()
    const baseUrl = teacherProfileBaseBySchool[school]
    if (!teacherName || !baseUrl) return ''

    const slug = teacherName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
    return `${baseUrl}/lessons-with-${slug}`
}

export const buildTeacherInfoCopyText = (assignedTeachers = []) => (
    (Array.isArray(assignedTeachers) ? assignedTeachers : [])
        .map((teacher) => {
            const teacherName = normalize(teacher.teacherName || teacher.Name)
            if (!teacherName) return ''
            const profileUrl = getTeacherProfileUrl(teacher)
            return profileUrl
                ? `Teacher: ${teacherName}\nProfile: ${profileUrl}`
                : `Teacher: ${teacherName}`
        })
        .filter(Boolean)
        .join('\n\n')
)

const calculateAge = (dob) => {
    const birthDate = new Date(dob)
    if (Number.isNaN(birthDate.getTime())) return ''
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDifference = today.getMonth() - birthDate.getMonth()
    if (
        monthDifference < 0
        || (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
        age -= 1
    }
    return String(age)
}

export const buildStudentInfoCopyText = (familyInfo = {}) => {
    const students = Array.isArray(familyInfo.students) ? familyInfo.students : []
    const studentText = students.map((student) => {
        const instruments = (Array.isArray(student.instrumentList) ? student.instrumentList : [])
            .map((instrument) => normalize(instrument?.name || instrument))
            .filter(Boolean)
            .join(', ') || 'Not specified'
        const age = calculateAge(student.dob)

        return [
            `Student: ${normalize(student.studentName) || 'Not provided'}`,
            `Age: ${age ? `${age} Years Old` : 'Age not provided'}`,
            `Instruments: ${instruments}`,
            `Experience: ${normalize(student.experienceLevel) || 'No experience'}`,
            `Lesson Length: ${Number(student.lessonLength) || 30} Minutes`,
        ].join('\n')
    }).join('\n\n')

    return [
        `Family Name: ${normalize(familyInfo.familyName) || 'Not provided'}`,
        `Primary Parent: ${formatParentName(familyInfo) || 'Not provided'}`,
        `Address: ${formatFamilyAddress(familyInfo.address) || 'Not provided'}`,
        `Email: ${normalize(familyInfo.primaryEmail) || 'Not provided'}`,
        `Phone: ${normalize(familyInfo.primaryCellphone) || 'Not provided'}`,
        studentText,
    ].filter(Boolean).join('\n')
}
